import { Mutex } from 'async-mutex'
import {
  canAttemptRefresh,
  getAuthStateFromLocalStorage,
  getCurrentAuthState,
  setAppSessionJwts,
  setRefreshInProgress,
} from '~/atoms/AuthenticationState'
import { getApiInst, getMangoApiClient } from '~/atoms/MangoApi'
import { isSessionExpired } from '~/atoms/SessionExpiredState'
import { domains } from '~/constants/domains'
import emitterService from '~/services/EventEmitter/EmitterService'
import {
  AuthenticationEmitterEventType,
  AuthenticationUpdatePayload,
} from '~/services/EventEmitter/types'
import Logger from '~/services/Logger'
import { AnalyticsNamespace } from '~/types/Analytics/AnalyticsNamespaces'
import { WebSocketAnalyticsEvents } from '~/types/Analytics/WebSocketAnalyticsEvents'
import {
  AuthenticationState,
  AuthenticationUpdateType,
} from '~/types/AuthenticationTypes'
import {
  WebSocketCommand,
  WebSocketCommandType,
  WebSocketEvent,
  WebSocketEventType,
} from '~/types/WebSocketTypes'
import { Analytics } from '~/utils/analytics'
import {
  countAppTabs,
  getBrowserContext,
  holdTabLock,
  isDocumentDiscarded,
  loadPlatformVersion,
} from '~/utils/browserContext'
import {
  getAccessTokenExpiryMs,
  getAccessTokenIssuedAtMs,
  isAccessTokenExpired,
} from '~/utils/functions'
import { OffThreadTimer, OffThreadTimerHandle } from '~/utils/offThreadTimer'

const NORMAL_CLOSURE = 1000
const STARTING_RETRY_IN_MS = 250
const MAX_RECONNECT_RETRIES = 10
// The chat-rtm server severs the connection when the handshake token's JWT
// exp passes, so the client must rotate to a fresh token before then.
const ROTATE_BEFORE_EXPIRY_MS = 60_000
// Random extra earliness so tabs sharing a token stagger: the first tab
// refreshes, later tabs adopt the persisted result instead of racing the
// refresh endpoint with the same refresh token.
const ROTATE_JITTER_MS = 30_000
const ROTATE_RETRY_IN_MS = 5_000
// A fresh token's iat is the issuer's clock at refresh time; receipt minus
// iat is this machine's skew. Reported once when it exceeds this.
const CLOCK_SKEW_REPORT_MS = 60_000
// A token that arrives from our own rotation refresh counts as fresh for this
// long; used to tell a just-issued short token from an old stored one.
const FRESH_TOKEN_WINDOW_MS = 30_000
// setTimeout treats delays above 2^31-1 as ~1ms; tokens that far from expiry
// don't need proactive rotation.
const MAX_TIMEOUT_DELAY_MS = 2 ** 31 - 1
// A recovery this fast, on the first attempt, tells us nothing the paired
// unexpected_closure event doesn't already carry, so it isn't tracked.
const ROUTINE_RECOVERY_MS = 5_000
// An open socket that hears nothing from the server for this long has missed
// a heartbeat PING (30 s tick). CUS-904: a device between the browser and the
// ALB passes the upgrade, then drops frames, then closes at 60 s; the browser
// only ever sees a 1006, so this timer is the client-side view of that path.
const SILENT_SOCKET_AFTER_MS = 45_000
// On a socket that has heard no PING by these offsets, send an unsolicited
// PONG. chat-rtm counts client frames on its close line, so frames= on the
// 60 s closes shows whether client->server still works after the stall.
const STALL_PROBE_AT_MS = [35_000, 50_000]

// Per-socket counters, held in the handler closures so a stale socket's late
// events never land on a newer socket's tally.
interface SocketStats {
  openedAtMs: number | null
  framesReceived: number
  pingsReceived: number
  silentTimer: OffThreadTimerHandle | null
  probeTimers: OffThreadTimerHandle[]
  probesSent: number
  // Armed on the page thread for the same 45 s as the worker-side silent
  // timer; how late it fires measures this tab's timer throttling.
  pageTimer: ReturnType<typeof setTimeout> | null
  pageTimerArmedAtMs: number | null
  pageTimerFiredAtMs: number | null
  hiddenAtOpenMs: number
  tabs: number | null
  // Page Lifecycle freezes during this socket's life. Chrome fails a frozen
  // page's WebSocket (error + 1006 here, 1001 at the server), so freezes and
  // 60 s closes should move together if freezing is the cause (CUS-904).
  freezes: number
}

// Result of a pre-reconnect credential check: 'ok' = verified/refreshed,
// 'unauthorized' = server rejected and refresh couldn't fix it (stop looping,
// interceptor drives logout), 'retry' = transient (network / cooldown), keep
// the backoff going.
type RefreshOutcome = 'ok' | 'unauthorized' | 'retry'

let _retryCreateTimeout: NodeJS.Timeout | null = null

interface Unsubscribe {
  (): void
}

type WebSocketEventCallback = (
  event: WebSocketEvent<WebSocketEventType>,
) => void

interface WebSocketServiceControl {
  /** Subscribes the given callback as a recipient of WebSocketEvents
   * @param callback The callback to be invoked when a WebSocketEvent is received
   * @returns An unsubscribe function that will remove the callback from the list of recipients
   */
  subscribe: (callback: WebSocketEventCallback) => Unsubscribe
  /** Subscribes to connection open/close (true on open, false on close). */
  subscribeToConnection: (callback: (connected: boolean) => void) => Unsubscribe
  /** Whether the socket is currently open. */
  isConnected: () => boolean
  /** Sends the given message to the WebSocket server */
  sendMessage: (message: WebSocketCommand<WebSocketCommandType>) => void
  /** Closes the WebSocket connection */
  close: () => void
  /** Reconnect attempts since the last successful connection (0 when healthy). */
  getReconnectAttempts: () => number
}

export default class WebSocketService implements WebSocketServiceControl {
  private static _instance: WebSocketService | null = null
  private _pbxId: number | null = null
  private _pbxUuid: string | null = null
  private _websocket: WebSocket | null = null
  private _messagesToBeSent: WebSocketCommand<WebSocketCommandType>[] = []
  private _listeners: ((event: WebSocketEvent<WebSocketEventType>) => void)[] =
    []
  // Connection-lifecycle listeners, kept SEPARATE from message `_listeners` so
  // message consumers (messaging, heartbeat) never see connection events. Used
  // by the deskphone-cleanup hook to arm its safety net when chat-rtm drops —
  // deskphone RINGING/HANGUP ride this socket, distinct from the SIP transport.
  private _connectionListeners: ((connected: boolean) => void)[] = []
  private _retryTimeOut = STARTING_RETRY_IN_MS
  private _numReconnectAttempts = 0
  private _logger = new Logger('WebSocketService')
  // pbx:user the logger was built for; Logger ships to CloudWatch only with
  // that metadata, and the singleton exists before either is known.
  private _loggerScope: string | null = null
  private _socketStats: SocketStats | null = null
  // Silent/probe timers run off the page thread; see OffThreadTimer.
  private _timers = new OffThreadTimer()
  // Time this document has spent hidden, so a close can report how much of
  // the socket's life the tab was in the background.
  private _hiddenTotalMs = 0
  private _hiddenSinceMs: number | null = null
  private _frozenAtMs: number | null = null
  private _currentAuthState: AuthenticationState | null = null
  private getCurrentAccessToken = () =>
    this._currentAuthState?.appSessionJwts?.access_token
  private _reconnecting = false
  private _creationMutex = new Mutex()
  private _analytics = new Analytics(AnalyticsNamespace.WEB_SOCKET)
  // Outage details for the ws_reconnect event (close code + time-to-reconnect).
  private _outageCloseCode: number | null = null
  private _outageStartedAtMs: number | null = null
  // Expiry of the current connection's handshake token; a 1006 at/after it is
  // the routine server sever, not an outage (see onWebSocketClose).
  private _handshakeTokenExpiryMs: number | null = null
  // ws_connected tracks cold connects only — rotations and recoveries reopen
  // the socket routinely.
  private _hasTrackedColdConnect = false
  private _rotateBeforeExpiryTimeout: NodeJS.Timeout | null = null
  // Client clock minus issuer clock, from the last token we know was freshly
  // issued (positive = client ahead). Expiry math uses the issuer's clock:
  // a client running ahead otherwise sees every fresh token as already
  // inside the rotation window and rotates again at once (PE-101).
  private _clockSkewMs = 0
  private _rotationRefreshStartedAtMs: number | null = null
  // The token that arrived from a refresh we requested, and when. Matched by
  // value and recency in scheduleRotateBeforeExpiry: value so a bailed
  // tryCreate cannot hand the mark to another token, recency so a reconnect
  // hours later with the same token (sleep/wake) still rotates as before.
  private _freshRefreshedToken: { token: string; receivedAtMs: number } | null =
    null
  private _hasTrackedClockSkew = false
  private _hasTrackedRotationSkipped = false
  // Highest eventId received this session; reconnects pass it as
  // ?lastEventId= so chat-rtm replays events missed during the gap.
  private _lastEventId: number | null = null

  private constructor() {
    window.addEventListener('offline', this.onOffline)
    window.addEventListener('online', this.onOnline)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    document.addEventListener('freeze', this.onPageFreeze)
    document.addEventListener('resume', this.onPageResume)
    this._hiddenSinceMs = document.hidden ? Date.now() : null
    holdTabLock()
    void loadPlatformVersion()
    window.addEventListener('focus', this.onWindowFocused)
    emitterService.subscribe({
      eventType: AuthenticationEmitterEventType.USER_AUTH_DATA_UPDATED,
      callback: this.onAuthStateUpdated,
    })
    emitterService.subscribe({
      eventType: AuthenticationEmitterEventType.USER_LOGGED_OUT,
      callback: () => this.tryCloseSocket(NORMAL_CLOSURE, true),
    })
  }

  // #region Static Accessor
  public static getInstance(
    pbxId: number,
    pbxUuid: string,
    userId?: number,
  ): WebSocketServiceControl {
    if (!WebSocketService._instance) {
      WebSocketService._instance = new WebSocketService()
    }

    const wssInst = WebSocketService._instance
    if (pbxId > 0 && userId !== undefined) {
      const loggerScope = `${pbxId}:${userId}`
      if (wssInst._loggerScope !== loggerScope) {
        wssInst._loggerScope = loggerScope
        wssInst._logger = new Logger('WebSocketService', { pbxId, userId })
      }
    }
    const hasPbxIdChanged = wssInst._pbxId !== pbxId
    const isWebSocketClosedAndNotReconnecting =
      !wssInst._websocket && !wssInst._reconnecting
    const shouldCreateNewSocket =
      !wssInst._websocket ||
      hasPbxIdChanged ||
      isWebSocketClosedAndNotReconnecting

    if (shouldCreateNewSocket) {
      // set the pbxids
      wssInst._pbxId = pbxId
      wssInst._pbxUuid = pbxUuid
      if (hasPbxIdChanged) {
        // eventIds are global, but a replay cursor from another pbx's stream
        // would skip that pbx's recent events - start fresh instead.
        wssInst._lastEventId = null
      }
      void wssInst.tryCreate()
    }

    return WebSocketService._instance
  }

  /**
   * Read-only reconnect-attempt count for diagnostics. Unlike getInstance(),
   * this never constructs the singleton or creates a socket as a side effect,
   * so it's safe to call during render (e.g. the status panel). Returns 0 when
   * no socket has been created yet.
   */
  public static getReconnectAttempts(): number {
    return WebSocketService._instance?._numReconnectAttempts ?? 0
  }
  // #endregion

  // #region Public Methods
  /** Reconnect attempts since the last successful connection (0 when healthy). */
  public getReconnectAttempts = (): number => this._numReconnectAttempts

  public sendMessage = (message: WebSocketCommand<WebSocketCommandType>) => {
    if (!this._websocket) {
      this._messagesToBeSent.push(message)
      return
    }

    let messageJson: string
    switch (this._websocket.readyState) {
      case WebSocket.CONNECTING:
        this._messagesToBeSent.push(message)
        break
      case WebSocket.OPEN:
        messageJson = JSON.stringify(message as Record<string, unknown>)

        if (message.eventType !== WebSocketCommandType.Pong) {
          this._logger.info(`Sending message: ${messageJson}`)
          this._logger.info(`----`)
        }

        this._websocket.send(messageJson)
        break
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        this._logger.info(
          'Message sent while socket is closed or closing - message queued until socket is open',
        )
        this._messagesToBeSent.push(message)

        //if reconnecting, don't try to recreate the socket
        if (!this._reconnecting) {
          void this.tryCreate()
        }
        break
      default:
        break
    }
  }

  public subscribe = (callback: WebSocketEventCallback): Unsubscribe => {
    this._logger.info(`Subscribing to ${callback.name}`)
    this._listeners.push(callback)

    return () => {
      const index = this._listeners.indexOf(callback)
      if (index === -1) {
        return
      }
      this._listeners.splice(index, 1)
    }
  }

  /**
   * Subscribe to chat-rtm connection open/close. Fires `true` on open and
   * `false` on close. Separate from `subscribe` (message events) so nothing in
   * the message path is affected. Returns an unsubscribe function.
   */
  public subscribeToConnection = (
    callback: (connected: boolean) => void,
  ): Unsubscribe => {
    this._connectionListeners.push(callback)

    return () => {
      const index = this._connectionListeners.indexOf(callback)
      if (index === -1) {
        return
      }
      this._connectionListeners.splice(index, 1)
    }
  }

  /** Whether the chat-rtm socket is currently open. */
  public isConnected = (): boolean =>
    this._websocket?.readyState === WebSocket.OPEN

  private notifyConnectionListeners = (connected: boolean) => {
    this._connectionListeners.forEach((listener) => {
      try {
        listener(connected)
      } catch (e) {
        this._logger.error('Connection listener threw', e as Error)
      }
    })
  }

  public close = () => {
    /**
     * Close with a "Normal Closure" code
     * https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
     */
    try {
      this.tryCloseSocket(NORMAL_CLOSURE, true)
    } catch (e: unknown) {
      this._logger.info('Web Socket failed to close', {
        closeError: e as Error,
        message: 'WEBSOCKET_CLOSE_FAILED',
        retryTimeout: this._retryTimeOut,
        error: e,
      })
    }
  }
  // #endregion

  private onOffline = () => {
    this._logger.info('Websocket offline')
    this.tryCloseSocket()
  }

  private onOnline = () => {
    //if we're already trying to reconnect, don't try to reconnect again
    if (this._reconnecting) {
      return
    }

    this._logger.info('Websocket online')
    this.tryCreate()
  }

  private onWindowFocused = () => {
    //if the websocket is in a disconnected state, try to reconnect
    if (
      !this._reconnecting &&
      (!this._websocket ||
        this._websocket.readyState === WebSocket.CLOSED ||
        this._websocket.readyState === WebSocket.CLOSING)
    ) {
      //already being handled by the tryCreate mechanism
      if (this._creationMutex.isLocked()) {
        return
      }

      this._logger.info('WSS disconnected on window focus - reconnecting...')
      void this.tryCreate()
    }
  }

  private tryCreate = async () => {
    //if no pbx has been set in our scope, the mutex is locked, don't try to create a new socket, just bail gracefully
    if (this._pbxId === 0 || this._creationMutex.isLocked()) {
      return
    }

    if (!canAttemptRefresh()) {
      // A usable token in state connects immediately; only wait out the
      // in-flight refresh (or cooldown) when the token actually needs it.
      const stateAccessToken =
        getCurrentAuthState().appSessionJwts?.access_token
      if (!stateAccessToken || isAccessTokenExpired(stateAccessToken)) {
        this._logger.info(
          'Auth refresh in progress, deferring WebSocket creation',
        )

        if (!_retryCreateTimeout) {
          _retryCreateTimeout = setTimeout(() => {
            _retryCreateTimeout = null
            this._logger.info('Retrying WebSocket creation after auth refresh')
            void this.tryCreate()
          }, 1000)
        }
        return
      }
    }

    const release = await this._creationMutex.acquire()
    try {
      // Checked after the await, so a resume that adopts a stored pair and
      // then clears the flag in the same tick still connects.
      if (isSessionExpired()) {
        this._logger.info('Session expired - not creating websocket')
        return
      }

      this._logger.info('tryCreate mutex acquired - creating websocket...')

      if (!('WebSocket' in window || 'MozWebSocket' in window)) {
        this._logger.info('WebSocket object not found in window, bailing...')
        return
      }

      //kill the current auth state changed registration, get the current copy of the user's auth data
      this._currentAuthState = getCurrentAuthState()

      const currentAccessToken = this.getCurrentAccessToken()
      if (!currentAccessToken) {
        this._logger.info(
          'No access token - WSS cannot initiate websocket connection.',
        )
        return
      }

      if (isAccessTokenExpired(currentAccessToken)) {
        this._logger.info('Access token expired, attempting refresh...')
        // Token is known-expired here, so only a verified/refreshed 'ok' yields
        // a usable token - connecting on a 'retry' or 'unauthorized' outcome
        // would just get the socket kicked.
        const outcome = await this.tryRefreshCredentials()
        if (outcome !== 'ok') {
          this._logger.info(
            `Credential check returned '${outcome}', deferring websocket creation`,
          )
          return
        }
        this._currentAuthState = getCurrentAuthState()
      }

      const domain = domains.CHAT_RTM()
      if (!domain) {
        this._logger.info(
          'No chat rtm domain found, unable to connect over WSS...',
        )
        return
      }

      const token = this.getCurrentAccessToken()
      const pbxScope = this._pbxUuid
        ? `${this._pbxId},${this._pbxUuid}`
        : `${this._pbxId}`

      //pbxScope is a comma-delimited string of pbxIds - for now we are only going to scope to one pbx at a time - in the future if we enable notifications on the application across all PBXs a user has access to, we will need to update this query string.
      // chat-rtm event replay (PR MangoVoice/chat-rtm#57): if `lastEventId` is
      // present, the server replays buffered events with a higher id before
      // emitting `connection`. The server-side ring buffer caps at 100 events
      // per PBX with a 5-minute TTL (see chat-rtm src/utils/eventReplayBuffer.ts
      // MAX_EVENTS_PER_PBX / MAX_AGE_MS). For disconnects longer than 5 minutes
      // or higher event volumes, replay is best-effort and the orphaned-call
      // watchdog + reconnect sweep in usePhone.ts handle the remainder.
      const lastEventIdParam =
        this._lastEventId !== null ? `&lastEventId=${this._lastEventId}` : ''
      // Construct the replacement before discarding the previous socket so a
      // constructor throw (or any bail above) never strands the service
      // socketless. chat-rtm allows overlapping connections per user, so the
      // brief overlap is safe.
      let socket: WebSocket
      try {
        socket = new WebSocket(
          `${domain}?token=${token}&pbxScope=${pbxScope}${lastEventIdParam}`,
        )
      } catch (error: any) {
        this._logger.error('WebSocket construction failed', error)
        return
      }

      // Dispose the replaced socket's callbacks so its close event can't
      // reach onWebSocketClose - it would misattribute the deliberate swap
      // as an outage (analytics + reconnect) and null out the new socket ref.
      if (this._websocket && this._websocket.readyState !== WebSocket.CLOSED) {
        this._logger.info('Websocket already exists, closing...')
        this.tryCloseSocket(NORMAL_CLOSURE, true)
      }
      // Per-socket connect state, captured in closures so a stale socket's
      // late close event can't be misattributed to a newer attempt.
      const connectStartedAtMs = Date.now()
      let socketOpened = false
      const stats: SocketStats = {
        openedAtMs: null,
        framesReceived: 0,
        pingsReceived: 0,
        silentTimer: null,
        probeTimers: [],
        probesSent: 0,
        pageTimer: null,
        pageTimerArmedAtMs: null,
        pageTimerFiredAtMs: null,
        hiddenAtOpenMs: 0,
        tabs: null,
        freezes: 0,
      }
      socket.onopen = () => {
        socketOpened = true
        const openedAtMs = Date.now()
        stats.openedAtMs = openedAtMs
        stats.hiddenAtOpenMs = this.hiddenTotalMs()
        stats.silentTimer = this._timers.set(() => {
          stats.silentTimer = null
          this.onSocketSilent(socket, stats)
        }, SILENT_SOCKET_AFTER_MS)
        stats.probeTimers = STALL_PROBE_AT_MS.map((atMs) =>
          this._timers.set(() => this.sendStallProbe(socket, stats), atMs),
        )
        stats.pageTimerArmedAtMs = openedAtMs
        stats.pageTimer = setTimeout(() => {
          stats.pageTimer = null
          stats.pageTimerFiredAtMs = Date.now()
        }, SILENT_SOCKET_AFTER_MS)
        void countAppTabs().then((tabs) => {
          stats.tabs = tabs
        })
        this.onWebSocketOpen(connectStartedAtMs)
      }
      socket.onmessage = (event) => {
        stats.framesReceived++
        this.onWebSocketMessageReceived(event, stats)
      }
      socket.onclose = (event) => {
        this.clearSocketTimers(stats)
        this.onWebSocketClose(event, socketOpened, stats)
      }
      socket.onerror = this.onWebSocketError
      // Assigned together: tryCloseSocket clears _socketStats' timer for the
      // socket it closes, so the two must always describe the same socket.
      this._websocket = socket
      this._socketStats = stats
      this.scheduleRotateBeforeExpiry(token)

      this._logger.info('tryCreate completed, waiting on Open...')
    } finally {
      this._logger.info('tryCreate mutex released...')
      release()
    }
  }

  private onAuthStateUpdated = ({
    newAuthState,
    updateType,
  }: AuthenticationUpdatePayload) => {
    this._logger.info('Auth State Changed')

    //if the websocket already exists, check to see if the access token has changed, if it has, set the new authState object, and proceed
    if (this._websocket) {
      if (
        newAuthState.appSessionJwts?.access_token ===
        this.getCurrentAccessToken()
      ) {
        return
      }

      this._currentAuthState = newAuthState
      if (updateType !== AuthenticationUpdateType.AppSessionJwt) {
        return
      }
    }

    const accessToken = newAuthState.appSessionJwts?.access_token
    if (!accessToken) {
      this._logger.info('No access token in auth update')
      return
    }
    this.noteTokenReceipt(accessToken)

    // A fresh token re-keys an open socket too: the server severs the
    // connection once the handshake token's exp passes.
    this._logger.info(
      this._websocket?.readyState === WebSocket.OPEN
        ? 'Access token changed while socket open - rotating connection'
        : 'Auth state updated, attempting websocket creation',
    )
    void this.tryCreate()
  }

  private clearRotateBeforeExpiryTimeout = () => {
    if (this._rotateBeforeExpiryTimeout) {
      clearTimeout(this._rotateBeforeExpiryTimeout)
      this._rotateBeforeExpiryTimeout = null
    }
  }

  // Only a token from a refresh we requested (the rotation's own, or the
  // reconnect path's verifyConnection) is known to be freshly issued; a token
  // adopted from storage may be minutes old, which would read as skew.
  private noteTokenReceipt = (accessToken: string) => {
    const startedAtMs = this._rotationRefreshStartedAtMs
    this._rotationRefreshStartedAtMs = null
    if (
      startedAtMs === null ||
      Date.now() - startedAtMs > FRESH_TOKEN_WINDOW_MS
    ) {
      return
    }
    this._freshRefreshedToken = {
      token: accessToken,
      receivedAtMs: Date.now(),
    }
    const issuedAtMs = getAccessTokenIssuedAtMs(accessToken)
    if (issuedAtMs === null) {
      return
    }
    this._clockSkewMs = Date.now() - issuedAtMs
    if (
      Math.abs(this._clockSkewMs) >= CLOCK_SKEW_REPORT_MS &&
      !this._hasTrackedClockSkew
    ) {
      this._hasTrackedClockSkew = true
      this._logger.warn('Client clock differs from the token issuer', {
        skewMs: this._clockSkewMs,
      })
      this._analytics.track(WebSocketAnalyticsEvents.ClockSkew, {
        skewMs: this._clockSkewMs,
      })
    }
  }

  // The issuer's clock, as far as we know it.
  private issuerNowMs = () => Date.now() - this._clockSkewMs

  private scheduleRotateBeforeExpiry = (handshakeToken?: string) => {
    this.clearRotateBeforeExpiryTimeout()
    const fresh = this._freshRefreshedToken
    const freshFromRefresh =
      fresh !== null &&
      handshakeToken === fresh.token &&
      Date.now() - fresh.receivedAtMs <= FRESH_TOKEN_WINDOW_MS

    const expiryMs = handshakeToken
      ? getAccessTokenExpiryMs(handshakeToken)
      : null
    this._handshakeTokenExpiryMs = expiryMs
    if (expiryMs === null || expiryMs <= this.issuerNowMs()) {
      // No parseable future expiry - the reconnect path recovers if the
      // server severs the connection.
      return
    }

    const jitterMs = Math.floor(Math.random() * ROTATE_JITTER_MS)
    // A token cannot have more life left than it was issued with, whatever
    // either clock says; the cap protects against a stale skew estimate.
    const issuedAtMs = handshakeToken
      ? getAccessTokenIssuedAtMs(handshakeToken)
      : null
    const remainingMs = Math.min(
      expiryMs - this.issuerNowMs(),
      issuedAtMs === null ? Infinity : expiryMs - issuedAtMs,
    )
    const delayMs = remainingMs - ROTATE_BEFORE_EXPIRY_MS - jitterMs
    if (delayMs > MAX_TIMEOUT_DELAY_MS) {
      return
    }

    // A token we just refreshed that is inside the window (by the full
    // jitter, not this connect's random draw) means the issuer hands out
    // tokens shorter than the window. Rotating again would loop every few
    // seconds; leave this one to the server's expiry sever and the reactive
    // reconnect, say so once, and only re-check storage for a token another
    // tab may have refreshed, so tabs sharing a session still converge.
    if (
      freshFromRefresh &&
      remainingMs <= ROTATE_BEFORE_EXPIRY_MS + ROTATE_JITTER_MS
    ) {
      if (!this._hasTrackedRotationSkipped) {
        this._hasTrackedRotationSkipped = true
        const props = { remainingMs, skewMs: this._clockSkewMs }
        this._logger.warn(
          'Refreshed token is already inside the rotation window - not rotating again',
          props,
        )
        this._analytics.track(WebSocketAnalyticsEvents.RotationSkipped, props)
      }
      this._rotateBeforeExpiryTimeout = setTimeout(
        () => {
          this._rotateBeforeExpiryTimeout = null
          if (this._websocket?.readyState === WebSocket.OPEN) {
            this.tryAdoptStoredToken()
          }
        },
        Math.max(remainingMs - ROTATE_RETRY_IN_MS, 0),
      )
      return
    }

    // A negative delay means the token is already inside the rotation window
    // at connect time; fire immediately and let the handler's retry loop
    // rotate once the socket opens.
    this._rotateBeforeExpiryTimeout = setTimeout(
      () => {
        this._rotateBeforeExpiryTimeout = null
        void this.onHandshakeTokenNearingExpiry()
      },
      Math.max(delayMs, 0),
    )
  }

  // Re-arm a short retry while the handshake token still has runway, so a
  // transient failure or cooldown doesn't forfeit the rotation window.
  private retryRotateBeforeExpiry = () => {
    const handshakeToken = this.getCurrentAccessToken()
    const expiryMs = handshakeToken
      ? getAccessTokenExpiryMs(handshakeToken)
      : null
    if (
      expiryMs === null ||
      this.issuerNowMs() + ROTATE_RETRY_IN_MS >= expiryMs
    ) {
      // Inside the final seconds the proactive path hands off to the
      // reactive reconnect at expiry rather than racing it.
      return
    }

    this.clearRotateBeforeExpiryTimeout()
    this._rotateBeforeExpiryTimeout = setTimeout(() => {
      this._rotateBeforeExpiryTimeout = null
      void this.onHandshakeTokenNearingExpiry()
    }, ROTATE_RETRY_IN_MS)
  }

  // Another tab sharing this session may have refreshed already; its token
  // pair is persisted. Adopting it rotates the socket without a network call
  // and keeps concurrent tabs from racing on the same refresh token.
  private tryAdoptStoredToken = (): boolean => {
    const storedJwts = getAuthStateFromLocalStorage().appSessionJwts
    const storedToken = storedJwts?.access_token
    const currentToken = this.getCurrentAccessToken()
    if (!storedToken || storedToken === currentToken) {
      return false
    }

    const storedExpiryMs = getAccessTokenExpiryMs(storedToken)
    const currentExpiryMs = currentToken
      ? getAccessTokenExpiryMs(currentToken)
      : null
    if (
      storedExpiryMs === null ||
      (currentExpiryMs !== null && storedExpiryMs <= currentExpiryMs)
    ) {
      return false
    }

    this._logger.info('Newer access token found in storage - rotating')
    setAppSessionJwts(storedJwts)
    return true
  }

  /**
   * Token refresh is otherwise reactive (401 interceptor), which fires only
   * after exp - the same moment the server severs this socket. Rotating needs
   * a fresh token BEFORE exp, so this is the one place that refreshes
   * proactively. Failures don't force logout because the interceptor's
   * onAuthFailure is the single source of truth for auth failure handling.
   */
  private onHandshakeTokenNearingExpiry = async () => {
    const readyState = this._websocket?.readyState
    if (readyState === WebSocket.CONNECTING) {
      this.retryRotateBeforeExpiry()
      return
    }
    if (readyState !== WebSocket.OPEN) {
      return
    }

    if (this.tryAdoptStoredToken()) {
      return
    }

    if (!canAttemptRefresh()) {
      this._logger.info(
        'Handshake token nearing expiry, refresh unavailable - retrying shortly',
      )
      this.retryRotateBeforeExpiry()
      return
    }

    this._logger.info(
      'Handshake token nearing expiry - refreshing credentials to rotate connection',
    )
    setRefreshInProgress(true)
    this._rotationRefreshStartedAtMs = Date.now()
    try {
      const refreshResult = await getApiInst().getRefreshToken()
      if (!refreshResult.ok) {
        this._logger.info('Pre-expiry token refresh failed - will retry')
        this.retryRotateBeforeExpiry()
      }
    } catch (error: any) {
      this._logger.error('Pre-expiry token refresh failed', error)
      this.retryRotateBeforeExpiry()
    } finally {
      // The refreshed token, if any, was broadcast synchronously inside
      // getRefreshToken and already noted; an unchanged token never reaches
      // noteTokenReceipt, so clear here rather than rely on it.
      this._rotationRefreshStartedAtMs = null
      setRefreshInProgress(false)
    }
  }

  private getPong = () => {
    return {
      eventType: WebSocketCommandType.Pong,
      eventTimestamp: new Date().toISOString(),
    } as WebSocketCommand<WebSocketCommandType.Pong>
  }

  // A PING settles the question both timers ask, so they go together.
  private clearSilentTimer = (stats: SocketStats) => {
    this._timers.clear(stats.silentTimer)
    stats.silentTimer = null
    stats.probeTimers.forEach((handle) => this._timers.clear(handle))
    stats.probeTimers = []
  }

  private clearSocketTimers = (stats: SocketStats) => {
    this.clearSilentTimer(stats)
    if (stats.pageTimer) {
      clearTimeout(stats.pageTimer)
      stats.pageTimer = null
    }
  }

  private onVisibilityChange = () => {
    if (document.hidden) {
      if (this._hiddenSinceMs === null) {
        this._hiddenSinceMs = Date.now()
      }
    } else if (this._hiddenSinceMs !== null) {
      this._hiddenTotalMs += Date.now() - this._hiddenSinceMs
      this._hiddenSinceMs = null
    }
  }

  // The freeze handler runs synchronously before Chrome freezes the page, so
  // it only records; the resume handler runs first when the page thaws,
  // ahead of the WebSocket events that were queued while frozen. Document
  // listeners outlive a replaced singleton, hence the instance guard.
  private onPageFreeze = () => {
    if (WebSocketService._instance !== this) {
      return
    }
    this._frozenAtMs = Date.now()
    if (this._socketStats) {
      this._socketStats.freezes++
    }
  }

  private onPageResume = () => {
    if (WebSocketService._instance !== this) {
      return
    }
    const frozenMs =
      this._frozenAtMs === null ? null : Date.now() - this._frozenAtMs
    this._frozenAtMs = null
    const socket = this._websocket
    const props = {
      frozenMs,
      socketState: socket?.readyState ?? null,
      socketLifeMs:
        this._socketStats?.openedAtMs == null
          ? null
          : Date.now() - this._socketStats.openedAtMs,
      visibility: document.visibilityState,
    }
    this._logger.info('Page resumed after a freeze', props)
    this._analytics.track(WebSocketAnalyticsEvents.PageResumed, props)
  }

  private hiddenTotalMs = () =>
    this._hiddenTotalMs +
    (this._hiddenSinceMs === null ? 0 : Date.now() - this._hiddenSinceMs)

  // Only while this is still the live socket and it has heard no PING.
  private sendStallProbe = (socket: WebSocket, stats: SocketStats) => {
    if (
      this._websocket !== socket ||
      socket.readyState !== WebSocket.OPEN ||
      stats.pingsReceived > 0
    ) {
      return
    }
    socket.send(JSON.stringify(this.getPong()))
    stats.probesSent++
    this._logger.info('Stall probe sent on a socket with no server PING', {
      afterMs: Date.now() - (stats.openedAtMs ?? Date.now()),
      probesSent: stats.probesSent,
    })
  }

  // Everything a close can say about where the socket died and what state
  // the tab was in. Read with chat-rtm's close line (frames=, pongs=).
  private closureContext = (stats?: SocketStats) => {
    const now = Date.now()
    return {
      lifeMs:
        stats?.openedAtMs === null || stats?.openedAtMs === undefined
          ? null
          : now - stats.openedAtMs,
      framesReceived: stats?.framesReceived ?? 0,
      pingsReceived: stats?.pingsReceived ?? 0,
      probesSent: stats?.probesSent ?? 0,
      pageTimerLateMs:
        stats?.pageTimerFiredAtMs == null || stats.pageTimerArmedAtMs == null
          ? null
          : stats.pageTimerFiredAtMs -
            stats.pageTimerArmedAtMs -
            SILENT_SOCKET_AFTER_MS,
      visibility: document.visibilityState,
      hiddenMs: stats ? this.hiddenTotalMs() - stats.hiddenAtOpenMs : null,
      wasDiscarded: isDocumentDiscarded(),
      tabs: stats?.tabs ?? null,
      freezes: stats?.freezes ?? 0,
      ...getBrowserContext(),
    }
  }

  // Once per socket, and only while it is still the live socket.
  private onSocketSilent = (socket: WebSocket, stats: SocketStats) => {
    if (this._websocket !== socket || socket.readyState !== WebSocket.OPEN) {
      return
    }
    // Real elapsed time, not the constant: a background tab throttles timers,
    // so the callback can run well after the 45 s it was armed for.
    const props = {
      afterMs: Date.now() - (stats.openedAtMs ?? Date.now()),
      framesReceived: stats.framesReceived,
    }
    this._logger.warn('Websocket open but no server PING received', props)
    this._analytics.track(WebSocketAnalyticsEvents.SilentSocket, props)
  }

  private onWebSocketMessageReceived = (
    { data }: MessageEvent,
    stats?: SocketStats,
  ) => {
    const message = JSON.parse(data) as WebSocketEvent<WebSocketEventType>

    if (
      typeof message.eventId === 'number' &&
      (this._lastEventId === null || message.eventId > this._lastEventId)
    ) {
      this._lastEventId = message.eventId
    }

    if (message.eventType === WebSocketEventType.Ping) {
      if (stats) {
        stats.pingsReceived++
        this.clearSilentTimer(stats)
      }
      this.sendMessage(this.getPong())
      // Also surface the heartbeat to subscribers so in-tree hooks can record
      // liveness for the status panel. The heartbeat is server-initiated (we
      // only reply with a Pong), so this is a "last heard from server" signal,
      // not a round-trip latency.
      this._listeners.forEach((listener) => listener(message))
    } else {
      this._listeners.forEach((listener) => listener(message))
      this._logger.debug('Web Socket Message Received', {
        message,
        listeners: this._listeners.length,
      })
    }
  }

  private onWebSocketOpen = (connectStartedAtMs: number) => {
    this._logger.info('Websocket opened')

    if (!this._hasTrackedColdConnect) {
      this._hasTrackedColdConnect = true
      this._analytics.track(WebSocketAnalyticsEvents.Connected, {
        durationMs: Date.now() - connectStartedAtMs,
      })
    }

    // Any recorded outage counts — recovery can also arrive via tryCreate()
    // from an auth-state update before the backoff loop runs (attempts 0).
    if (this._outageStartedAtMs !== null) {
      const durationMs = Date.now() - this._outageStartedAtMs
      // Outage rate is measured from unexpected_closure, which fires for every
      // outage including the ones that never recover. This event only adds the
      // recovery cost, so it fires when that cost is worth a row.
      if (this._numReconnectAttempts > 1 || durationMs >= ROUTINE_RECOVERY_MS) {
        this._analytics.track(WebSocketAnalyticsEvents.Reconnect, {
          code: this._outageCloseCode,
          attempts: this._numReconnectAttempts,
          durationMs,
        })
      }
    }
    this._outageCloseCode = null
    this._outageStartedAtMs = null

    this._messagesToBeSent.forEach((message) => {
      this.sendMessage(message)
    })

    this._messagesToBeSent = []
    this._retryTimeOut = STARTING_RETRY_IN_MS
    this._reconnecting = false
    this._numReconnectAttempts = 0

    this.notifyConnectionListeners(true)
  }

  // Verify (and, via the 401 interceptor, refresh) the credentials before we
  // reconnect. We deliberately do NOT short-circuit on the local
  // isAccessTokenExpired() check: the chat-rtm server severs an auth-rejected
  // socket with a raw HTTP 401 + socket.destroy(), which the browser surfaces
  // as a bare 1006 with no reason - indistinguishable from a network drop, and
  // present even when our own token still parses as unexpired (clock skew, a
  // server-side revoke, or a pbx-scope change). Reusing that token would just
  // loop (CUS-637). verifyConnection() is the only signal that can tell an auth
  // rejection ('unauthorized') from a transient outage ('network_error').
  private tryRefreshCredentials = async (): Promise<RefreshOutcome> => {
    if (isSessionExpired()) {
      return 'unauthorized'
    }

    if (!canAttemptRefresh()) {
      // A refresh is already in flight (the interceptor, or another tab) or
      // we're in cooldown. Not a failure - keep the backoff loop alive.
      this._logger.info(
        'Cannot verify credentials now (cooldown or in progress) - will retry',
      )
      return 'retry'
    }

    this._logger.info('Verifying credentials before reconnect...')
    setRefreshInProgress(true)
    // A token the 401 interceptor refreshes here is as fresh as a rotation's
    // and teaches the skew the same way.
    this._rotationRefreshStartedAtMs = Date.now()

    try {
      const apiClient = getMangoApiClient()
      const { connected, reason } = await apiClient.auth.verifyConnection()

      if (connected) {
        this._logger.info('Credentials verified')
        return 'ok'
      }

      if (reason === 'unauthorized') {
        // The 401 interceptor already attempted a refresh and, on failure,
        // fired onAuthFailure (the single source of truth for logout).
        // Reconnecting with this token would just re-trigger the server kick.
        this._logger.info(
          'Credentials rejected - abandoning reconnect (auth failure handled by interceptor)',
        )
        return 'unauthorized'
      }

      // network_error: transient. Keep retrying with the existing backoff.
      this._logger.info('Credential check failed (network) - will retry')
      return 'retry'
    } catch (error: any) {
      this._logger.error('Credential verification threw', error)
      return 'retry'
    } finally {
      this._rotationRefreshStartedAtMs = null
      setRefreshInProgress(false)
    }
  }

  private onWebSocketClose = async (
    event: CloseEvent,
    socketOpened: boolean,
    stats?: SocketStats,
  ) => {
    // One snapshot, so the log line and the analytics event agree exactly.
    const context = socketOpened ? this.closureContext(stats) : undefined
    this._logger.info(`Websocket closed with code: ${event.code}`, context)

    // Fire once for every close, ahead of the branchy outage/reconnect logic
    // below, so connection subscribers always see the drop.
    this.notifyConnectionListeners(false)

    if (event.code !== NORMAL_CLOSURE) {
      // chat-rtm severs with a raw 1006 once the handshake token's exp passes;
      // when proactive rotation misses that window (background-tab timer
      // throttling, sleep/wake) the close is routine, not an outage.
      const isExpectedTokenExpiry =
        event.code === 1006 &&
        this._handshakeTokenExpiryMs !== null &&
        this.issuerNowMs() >=
          this._handshakeTokenExpiryMs -
            ROTATE_BEFORE_EXPIRY_MS -
            ROTATE_JITTER_MS

      if (!isExpectedTokenExpiry && socketOpened) {
        // Analytics fire once per outage; retry churn within it is reported
        // as `attempts` on ws_reconnect, and offline-machine closes are
        // covered by wp_network_disconnected.
        if (this._outageStartedAtMs === null) {
          if (navigator.onLine) {
            this._analytics.track(WebSocketAnalyticsEvents.UnexpectedClosure, {
              code: event.code,
              reason: event.reason,
              ...context,
            })
          }
          this._outageStartedAtMs = Date.now()
          this._outageCloseCode = event.code
        }
      } else if (!isExpectedTokenExpiry && !socketOpened) {
        // A close before the socket ever opened is a failed connect attempt;
        // cold connects only — backoff retries are covered by ws_reconnect.
        if (this._numReconnectAttempts === 0 && navigator.onLine) {
          this._analytics.track(WebSocketAnalyticsEvents.ConnectFailed, {
            code: event.code,
            reason: event.reason,
          })
        }
      }
    }

    if (event.code === 1006) {
      console.trace({ event })
      // https://stackoverflow.com/questions/19304157/getting-the-reason-why-websockets-closed-with-close-code-1006
      this._logger.warn(
        'Abnormal closure detected (1006) - likely we did something fundamentally wrong',
      )
    }

    if (!event.code || event.code === NORMAL_CLOSURE) {
      this._websocket = null
      return
    }

    //something else caused the issue, try to reconnect through the normal reconnect mechanism with exponential backoff
    //the next attempt to connect will be made when the app tries to send a message
    if (this._numReconnectAttempts > MAX_RECONNECT_RETRIES) {
      this._reconnecting = false
      this._logger.info(
        'Websocket failed to reconnect and has exhausted all retries',
        {
          message: 'WEBSOCKET_RECONNECT_FAILED_RETRIES_EXHAUSTED',
        },
      )
      return
    }

    await this.handleTryReconnect()
  }

  private handleTryReconnect = async () => {
    // Prevent multiple concurrent reconnection attempts
    if (this._reconnecting) {
      this._logger.info('Reconnection already in progress, skipping')
      return
    }

    this._reconnecting = true

    try {
      //wait for the timeout time
      await new Promise((resolve) => {
        setTimeout(resolve, this._retryTimeOut)
      })

      //after the wait, check to see if we've already reconnected
      if (this._websocket?.readyState === WebSocket.OPEN) {
        this._logger.info(
          'Websocket reconnect sequence stopped - socket is open.',
        )
        return
      }

      this._logger.info(
        `Websocket reconnect attempt ${this._numReconnectAttempts} of ${MAX_RECONNECT_RETRIES}`,
      )
      this._numReconnectAttempts++
      this._retryTimeOut += this._retryTimeOut

      const outcome = await this.tryRefreshCredentials()

      if (outcome === 'unauthorized') {
        //the server authoritatively rejected the credentials and the 401
        //interceptor's refresh could not fix it - stop looping. onAuthFailure
        //(the interceptor) drives logout; we just dispose this socket.
        this.tryCloseSocket(undefined, true)
        this._logger.info(
          'Websocket reconnect abandoned - credentials rejected',
        )
        return
      }

      //'ok' (verified/refreshed) or 'retry' (transient network error / refresh
      //cooldown) both reconnect: on 'ok' with the fresh token, on 'retry' the
      //socket attempt is itself the retry - if the network is still down it
      //fails, closes, and the backoff schedules the next attempt. Only a
      //definitive 'unauthorized' stops the loop.
      //always close before attempting reconnection; dispose callbacks so a
      //discarded CONNECTING socket's abort can't fire spurious analytics or
      //recurse into another reconnect
      this.tryCloseSocket(NORMAL_CLOSURE, true)
      void this.tryCreate()
    } finally {
      // Always reset reconnecting flag
      this._reconnecting = false
    }
  }

  private onWebSocketError = (event: Event) => {
    this._logger.info(`Websocket errored with event type: ${event.type}`, {
      event,
    })
    // Don't close if already reconnecting to prevent recursion
    if (!this._reconnecting) {
      this.tryCloseSocket()
    }
  }

  private tryCloseSocket = (code?: number, disposeCallbacks = false) => {
    if (this._websocket === null) {
      return
    }
    this._logger.info('Websocket closing & cleaning up...')

    if (_retryCreateTimeout) {
      clearTimeout(_retryCreateTimeout)
      _retryCreateTimeout = null
    }

    this.clearRotateBeforeExpiryTimeout()
    if (this._socketStats) {
      this.clearSocketTimers(this._socketStats)
    }

    //remove all listeners and remove the reference to the current websocket instance
    if (disposeCallbacks) {
      try {
        this._websocket.onopen = null
        this._websocket.onmessage = null
        this._websocket.onclose = null
        this._websocket.onerror = null
      } catch {
        //do nothing - we don't care if this throws an error but I don't want it to squawk
        //we just need to make sure that the listeners are removed
      }
    }

    //close can be safely called when the websocket is in any connection state
    this._websocket.close(code)
    this._websocket = null
  }
}
